/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : LegalCustomDatePicker.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Divider,
  IconButton,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import PropTypes from "prop-types";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import InfoButton from "../../../infoButton/InfoButton";
export default function LegalCustomDatePicker({
  label = "",
  effectiveFrom,
  setEffectiveFrom,
  validTill,
  setValidTill,
  required,
  infoname,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const compactDatePickerSx = {
    width: "120px !important",
    height: "250px !important",
    "& .MuiPickersLayout-root": {
      minWidth: "0 !important",
      maxWidth: "120px !important",
    },
    "& .MuiPickersLayout-contentWrapper": {
      padding: 0,
    },
    "& .MuiPickersCalendarHeader-switchViewButton": {
      display: "none",
    },
    "& .MuiPickersCalendarHeader-root": {
      position: "relative",
      paddingLeft: "1px",
      paddingRight: "1px",
      justifyContent: "space-between",
      minHeight: "30px",
      marginBottom: 0,
    },
    "& .MuiPickersCalendarHeader-labelContainer": {
      position: "absolute",
      left: "50%",
      transform: "translateX(-50%)",
    },
    "& .MuiPickersCalendarHeader-label": {
      fontSize: "14px",
    },
    "& .MuiPickersArrowSwitcher-root": {
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
    },
    "& .MuiPickersArrowSwitcher-spacer": {
      display: "none",
    },
    "& .MuiPickersArrowSwitcher-button": {
      padding: 0,
    },
    "& .MuiDayCalendar-header": {
      gap: "0px",
      justifyContent: "space-between",
    },
    "& .MuiDayCalendar-weekContainer": {
      margin: "0px",
      justifyContent: "space-between",
    },
    "& .MuiPickersDay-root": {
      fontSize: "12px",
      width: "70px",
      height: "30px",
      margin: "0px",
      borderRadius: "0px !important",
    },
    "& .MuiDayCalendar-weekDayLabel": {
      fontSize: "12px",
      width: "70px",
      height: "30px",
      margin: "0px",
      borderRadius: "0px !important",
    },
    "& .MuiPickersDay-root.Mui-selected": {
      backgroundColor: "#000000",
      color: "#FFFFFF",
      "&:hover": {
        backgroundColor: "#000000",
      },
      borderRadius: "0px !important",
    },
    "& .MuiPickersDay-root:not(.Mui-selected)": {
      borderRadius: "0px !important",
    },
    "& .MuiPickersYear-yearButton.Mui-selected": {
      backgroundColor: "#000000 !important",
      color: "#FFFFFF !important",
      "&:hover": {
        backgroundColor: "#000000 !important",
      },
      borderRadius: "0px !important",
    },
    "& .MuiPickersMonth-monthButton.Mui-selected": {
      backgroundColor: "#000000 !important",
      color: "#FFFFFF !important",
      "&:hover": {
        backgroundColor: "#000000 !important",
      },
      borderRadius: "0px !important",
    },
  };

  let dateDisplayValue = "";
  if (effectiveFrom) {
    const fromStr = effectiveFrom.format("MM-YYYY");
    const tillStr = validTill ? ` — ${validTill.format("MM-YYYY")}` : "";
    dateDisplayValue = `${fromStr}${tillStr}`;
  }

  return (
    <Box>
      {label && (
        <Typography
          sx={{
            mb: 0.5,
            color: "#6f7174",
            fontSize: "0.9rem",
            fontWeight: 500,
            display: "flex",
          }}
        >
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
          <Box sx={{ width: 5, height: 5 }}>
            {infoname?.length > 0 ? (
              <InfoButton
                dimension={{ width: 15, height: 15 }}
                infoKey={infoname}
                color={"black"}
              />
            ) : null}
          </Box>
        </Typography>
      )}

      <TextField
        size="small"
        placeholder="MM/YYYY — MM/YYYY"
        value={dateDisplayValue}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {(effectiveFrom || validTill) && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEffectiveFrom(null);
                    setValidTill(null);
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16, color: "#000" }} />
                </IconButton>
              )}
              <CalendarMonthIcon
                sx={{ fontSize: 18, color: "#000", cursor: "pointer" }}
              />
            </Box>
          ),
        }}
        sx={{
          width: "100%",

          cursor: "pointer",
          "& .MuiOutlinedInput-root": {
            height: "100%",
            fontSize: "13px",

            "& fieldset": { borderColor: "#000", borderRadius: "0px" },
            "&:hover fieldset": { borderColor: "#000" },
            "&.Mui-focused fieldset": { borderColor: "#000" },
          },
        }}
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: { sx: { overflow: "visible", borderRadius: "0px" } },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Box sx={{ display: "flex", p: 0 }}>
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={effectiveFrom}
              onChange={(newValue) => {
                setEffectiveFrom(newValue.startOf("month"));

                if (newValue && validTill) setAnchorEl(null);
              }}
              views={["year", "month"]} // <--- month + year only
              maxDate={validTill || undefined}
              slotProps={{ actionBar: { actions: [] } }}
              sx={compactDatePickerSx}
            />

            <Divider orientation="vertical" flexItem sx={{ mt: "16px" }} />

            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={validTill}
              onChange={(newValue) => {
                setValidTill(newValue?.startOf("month") || null);

                if (effectiveFrom && newValue) setAnchorEl(null);
              }}
              views={["year", "month"]} // <--- month + year only
              minDate={effectiveFrom || undefined}
              slotProps={{ actionBar: { actions: [] } }}
              sx={compactDatePickerSx}
            />
          </Box>
        </LocalizationProvider>
      </Popover>
    </Box>
  );
}

LegalCustomDatePicker.propTypes = {
  label: PropTypes.string,
  effectiveFrom: PropTypes.object,
  setEffectiveFrom: PropTypes.func,
  validTill: PropTypes.object,
  setValidTill: PropTypes.func,
  required: PropTypes.bool,
  infoname: PropTypes.string,
};
